import React from 'react';
import PropTypes from 'prop-types';
import { Popover, MenuList, MenuItem, ListItemIcon, ListItemText, Typography, Box, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from '../Iconify';

const SectionLabel = ({ children }) => (
    <Typography
        variant="overline"
        sx={{
            px: 1.5,
            pt: 1.5,
            pb: 0.5,
            display: 'block',
            color: 'text.disabled',
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
        }}
    >
        {children}
    </Typography>
);

SectionLabel.propTypes = {
    children: PropTypes.node,
};

const OrDivider = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5 }}>
        <Divider sx={{ flexGrow: 1, borderStyle: 'dashed' }} />
        <Typography variant="caption" sx={{ px: 1, color: 'text.disabled', fontWeight: 600 }}>
            or
        </Typography>
        <Divider sx={{ flexGrow: 1, borderStyle: 'dashed' }} />
    </Box>
);

const SectionDivider = () => (
    <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />
);

const StyledMenuItem = ({ icon, iconColor, label, subtitle, onClick, endIcon }) => (
    <MenuItem
        onClick={onClick}
        sx={{
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
            },
        }}
    >
        <ListItemIcon
            sx={{
                width: 32,
                height: 32,
                minWidth: 32,
                mr: 1.5,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(iconColor, 0.12),
                color: iconColor,
            }}
        >
            <Iconify icon={icon} width={20} height={20} />
        </ListItemIcon>
        <ListItemText
            primary={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {label}
                </Typography>
            }
            secondary={
                subtitle ? (
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        {subtitle}
                    </Typography>
                ) : null
            }
        />
        {endIcon && (
            <Iconify icon={endIcon} width={16} height={16} sx={{ color: 'text.disabled', ml: 0.5 }} />
        )}
    </MenuItem>
);

StyledMenuItem.propTypes = {
    icon: PropTypes.string.isRequired,
    iconColor: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    onClick: PropTypes.func,
    endIcon: PropTypes.string,
};

StyledMenuItem.defaultProps = {
    subtitle: '',
    onClick: () => {},
    endIcon: '',
};

const CustomMenuItem = ({
    anchorEl,
    open,
    onClose,
    handleClickNewLead,
    handleClickCallScriptClick,
    handleClickConsentV1Click,
    handleClickConsentV2Click,
    handleClickStripePaymentClick,
    handleReviewClick,
    handleClickNewCampaign,
    handleClickBookingWhatsApp,
    dentallyEnabled,
    handleClickBookDentally,
    v1Enabled,
    v2Enabled,
}) => (
    // useFormsFlags enforces V2-supersedes-V1, so v1Enabled and v2Enabled
    // are mutually exclusive — at most one of these menu items will render.
    <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
            paper: {
                sx: {
                    width: 260,
                    borderRadius: 1.5,
                    p: 1,
                    mt: 0.5,
                    boxShadow: (theme) => theme.customShadows?.dropdown || theme.shadows[8],
                },
            },
        }}
    >
        <MenuList disablePadding>
            {/* Add Lead Section */}
            <SectionLabel>Add Lead</SectionLabel>
            <StyledMenuItem
                icon="solar:user-plus-bold-duotone"
                iconColor="#00A76F"
                label="Create Lead"
                subtitle="Enter details manually"
                onClick={handleClickNewLead}
            />
            {/* Book with Dentally */}
            {dentallyEnabled && (
                <>
                    <OrDivider />
                    <StyledMenuItem
                        icon="mdi:calendar-plus"
                        iconColor="#00AB55"
                        label="Book with Dentally"
                        subtitle="Create Dentally appointment"
                        onClick={handleClickBookDentally}
                    />
                </>
            )}
            <OrDivider />
            <StyledMenuItem
                icon="solar:phone-calling-bold-duotone"
                iconColor="#00A76F"
                label="Call Script"
                subtitle="Guided conversation flow"
                onClick={handleClickCallScriptClick}
                endIcon="eva:chevron-right-fill"
            />

            <SectionDivider />

            {/* Marketing Section */}
            <SectionLabel>Marketing</SectionLabel>
            <StyledMenuItem
                icon="solar:speaker-minimalistic-bold-duotone"
                iconColor="#8E33FF"
                label="New Campaign"
                onClick={handleClickNewCampaign}
            />

            <SectionDivider />

            {/* Send Link Section */}
            <SectionLabel>Send Link</SectionLabel>
            <StyledMenuItem
                icon="solar:calendar-bold-duotone"
                iconColor="#22C55E"
                label="Booking via WhatsApp"
                onClick={handleClickBookingWhatsApp}
            />
            <StyledMenuItem
                icon="solar:card-bold-duotone"
                iconColor="#FFAB00"
                label="Payment Link"
                onClick={handleClickStripePaymentClick}
            />
            {v1Enabled && (
                <StyledMenuItem
                    icon="solar:document-text-bold-duotone"
                    iconColor="#0078FF"
                    label="Consent Form"
                    onClick={handleClickConsentV1Click}
                />
            )}
            {v2Enabled && (
                <StyledMenuItem
                    icon="solar:document-text-bold-duotone"
                    iconColor="#0078FF"
                    label="Forms/Consent"
                    onClick={handleClickConsentV2Click}
                />
            )}
            <StyledMenuItem
                icon="solar:star-bold-duotone"
                iconColor="#FF5630"
                label="Feedback Request"
                onClick={handleReviewClick}
            />
        </MenuList>
    </Popover>
);

export default CustomMenuItem;

CustomMenuItem.propTypes = {
    anchorEl: PropTypes.object,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    handleClickNewLead: PropTypes.func,
    handleClickCallScriptClick: PropTypes.func,
    handleClickConsentV1Click: PropTypes.func,
    handleClickConsentV2Click: PropTypes.func,
    handleClickStripePaymentClick: PropTypes.func,
    handleReviewClick: PropTypes.func,
    handleClickNewCampaign: PropTypes.func,
    handleClickBookingWhatsApp: PropTypes.func,
    dentallyEnabled: PropTypes.bool,
    handleClickBookDentally: PropTypes.func,
    v1Enabled: PropTypes.bool,
    v2Enabled: PropTypes.bool,
};

CustomMenuItem.defaultProps = {
    anchorEl: null,
    handleClickNewLead: () => {},
    handleClickCallScriptClick: () => {},
    handleClickConsentV1Click: () => {},
    handleClickConsentV2Click: () => {},
    handleClickStripePaymentClick: () => {},
    handleReviewClick: () => {},
    handleClickNewCampaign: () => {},
    handleClickBookingWhatsApp: () => {},
    dentallyEnabled: false,
    handleClickBookDentally: () => {},
    v1Enabled: false,
    v2Enabled: false,
};
